import type { ReactNode } from 'react';
import styles from './toggle.module.css';
import { cx } from './cx';

/**
 * An on/off setting — a `<button role="switch">` with `aria-checked`.
 *
 * `role="switch"` rather than a checkbox, and the difference is not cosmetic: a checkbox says "include
 * this when you submit", a switch says "this takes effect now", and every use here takes effect now.
 * Screen readers read it as "on/off" instead of "checked/unchecked", which is the sentence that
 * matches what the control does.
 *
 * The label is a real element beside the track rather than a `title`, and the whole thing is one
 * button, so a click anywhere on it flips the setting — the failure mode of a switch built as a bare
 * track is that the label is the part people aim at and the part that does nothing.
 */
export interface ToggleProps {
  readonly id: string;
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  /** A sentence under the label. Wired through `aria-describedby`, so it is read with the control. */
  readonly detail?: ReactNode;
  /**
   * Keep the label out of sight — for a settings row whose title already names the control. The
   * accessible name is unchanged; a switch with no name would announce as "on".
   */
  readonly hideLabel?: boolean;
  /** An external description (a settings row's own detail line), when `detail` is not rendered here. */
  readonly describedBy?: string;
  readonly disabled?: boolean;
  readonly className?: string;
}

export function Toggle({
  id,
  label,
  checked,
  onChange,
  detail,
  hideLabel,
  describedBy,
  disabled,
  className,
}: ToggleProps) {
  const detailId = `${id}-detail`;
  return (
    <div className={cx(styles.row, className)}>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        aria-describedby={detail == null ? describedBy : detailId}
        className={styles.button}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      >
        <span className={cx(styles.track, checked && styles.trackOn)} aria-hidden="true">
          <span className={styles.knob} />
        </span>
        <span className={hideLabel === true ? styles.labelHidden : styles.label}>{label}</span>
      </button>
      {detail == null ? null : (
        <p className={styles.detail} id={detailId}>
          {detail}
        </p>
      )}
    </div>
  );
}
